<template>
  <IdentityShell>
    <template #masthead>
      <IdentityWelcomeMasthead />
    </template>

    <template #title>{{
      $t('auth.user.directoryEntryPage.title', 'Find your organization')
    }}</template>
    <template #lede>
      {{
        $t(
          'auth.user.directoryEntryPage.lede',
          "Enter the email you use on Communal. We'll send a 6-digit code to confirm it's you, then take you to the right place."
        )
      }}
    </template>

    <AlertDanger v-if="firstError" show class="mb-3">
      {{ firstError }}
    </AlertDanger>

    <FormBase @submit.prevent="onSubmit">
      <FormGroupBase
        class="mb-3"
        :label="$t('auth.user.directoryEntryPage.emailLabel', 'Email')"
        :state="fieldState('email')"
        :invalid-feedback="fieldError('email')"
      >
        <InputEmail
          v-model="email"
          autocomplete="email"
          autofocus
          :placeholder="
            $t(
              'auth.user.directoryEntryPage.emailPlaceholder',
              'you{\'@\'}example.com'
            )
          "
          :state="fieldState('email')"
        />
      </FormGroupBase>

      <ButtonPrimary type="submit" block :disabled="submitting">
        {{
          submitting
            ? $t('auth.user.directoryEntryPage.sendingCode', 'Sending code…')
            : $t(
                'auth.user.directoryEntryPage.emailMeCode',
                'Email me a sign-in code'
              )
        }}
      </ButtonPrimary>
    </FormBase>

    <template v-if="hasSocialProviders">
      <div class="directory-entry__divider" aria-hidden="true">
        <span>{{ $t('auth.user.directoryEntryPage.or', 'or') }}</span>
      </div>

      <AuthSocialButtons intent="login" class="directory-entry__social" />
    </template>
  </IdentityShell>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';

import { FormBase, FormGroupBase } from '@/components/base/Form';
import { AuthSocialButtons } from '@/components/feature/Auth';
import {
  IdentityShell,
  IdentityWelcomeMasthead,
} from '@/components/feature/Identity';
import { AlertDanger } from '@/components/ui/Alert';
import { ButtonPrimary } from '@/components/ui/Button';
import { InputEmail } from '@/components/ui/Form';
import { useIdentityForm } from '@/composables/useIdentityForm';

const email = ref('');
const hasSocialProviders = computed(() => {
  const social = (window as unknown as Record<string, unknown>)
    .socialProviders as { google?: boolean } | undefined;

  return Boolean(social?.google);
});

const { firstError, fieldState, fieldError, submitting, submit } =
  useIdentityForm({ url: '/login' });

async function onSubmit() {
  await submit({ email: email.value });
}
</script>

<style lang="scss" scoped>
.directory-entry__divider {
  position: relative;
  text-align: center;
  margin: 1.5rem 0 1rem;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: rgba(15, 23, 42, 0.42);

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    border-top: 1px solid #e6e8ec;
  }

  span {
    position: relative;
    background: #fff;
    padding: 0 0.875rem;
  }
}

.directory-entry__social {
  margin-bottom: 0;
}
</style>
